import { useState } from "react";
import { Link } from "react-router-dom";
import { courses, academyStats, academyTestimonials } from "@/mocks/academyData";
import CourseCard from "./components/CourseCard";
import EnrollModal from "./components/EnrollModal";
import ConsultationForm from "./components/ConsultationForm";

const categories = ["All", "Artificial Intelligence", "Cybersecurity", "Cloud Computing", "Data Analytics", "Digital Marketing", "App Development"];
const levels = ["All Levels", "Beginner", "Intermediate", "Advanced"];

export default function AcademyPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeLevel, setActiveLevel] = useState("All Levels");
  const [enrollCourse, setEnrollCourse] = useState<typeof courses[0] | null>(null);

  const filtered = courses.filter((c) => {
    const catMatch = activeCategory === "All" || c.category === activeCategory;
    const lvlMatch = activeLevel === "All Levels" || c.level === activeLevel;
    return catMatch && lvlMatch;
  });

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-20 bg-black overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=online%20education%20technology%20learning%20abstract%20dark%20background%20glowing%20knowledge%20network%20futuristic%20minimal%20cinematic%20wide&width=1440&height=600&seq=achero&orientation=landscape"
            alt="CPI Academy"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/85 to-black/60" />
        </div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C8F135]/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#C8F135]/10 border border-[#C8F135]/20 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-graduation-cap-line text-[#C8F135] text-sm" />
              <span className="text-[#C8F135] text-xs font-semibold uppercase tracking-wider">CPI Academy</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-5 leading-tight">
              Level Up Your
              <br />
              <span style={{color: "#C8F135"}}>Tech Career</span>
            </h1>
            <p className="text-gray-300 text-lg leading-relaxed mb-8 max-w-xl">
              Live online courses taught by CPI Tech&apos;s own industry experts. Real skills, real projects, real results: from AI to cybersecurity to cloud architecture.
            </p>
            <div className="flex flex-wrap gap-4">
              {academyStats.map((s) => (
                <div key={s.label} className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl px-5 py-3 text-center">
                  <div className="text-2xl font-black text-white">{s.value}</div>
                  <div className="text-gray-400 text-xs mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why CPI Academy */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: "ri-live-line", title: "Live Online Classes", desc: "Real-time sessions with instructors. Ask questions, get answers instantly." },
              { icon: "ri-video-line", title: "Recorded Replays", desc: "Miss a session? Every class is recorded and available on-demand forever." },
              { icon: "ri-medal-line", title: "Industry Certificates", desc: "Earn recognized certificates that employers actually value." },
              { icon: "ri-briefcase-line", title: "Career Support", desc: "Resume reviews, mock interviews, and job placement assistance included." },
            ].map((f) => (
              <div key={f.title} className="flex flex-col items-start gap-3 p-6 bg-slate-50 rounded-xl border border-slate-100">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#C8F135]/20">
                  <i className={`${f.icon} text-xl`} style={{color: "#9bc400"}} />
                </div>
                <h3 className="font-bold text-slate-900 text-sm">{f.title}</h3>
                <p className="text-slate-500 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Catalog */}
      <section className="py-16 bg-slate-50" id="courses">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-1.5 mb-5">
              <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Course Catalog</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              Find Your <span style={{color: "#9bc400"}}>Perfect Course</span>
            </h2>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                    activeCategory === cat ? "bg-black text-[#C8F135]" : "bg-white text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="flex gap-2 sm:ml-auto">
              {levels.map((lvl) => (
                <button
                  key={lvl}
                  onClick={() => setActiveLevel(lvl)}
                  className={`px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                    activeLevel === lvl ? "bg-black text-[#C8F135]" : "bg-white text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {lvl}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <i className="ri-search-line text-4xl mb-3 block" />
              <p>No courses match your filters. Try a different combination.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((course) => (
                <div key={course.id} className="relative">
                  <CourseCard course={course} />
                  <button
                    onClick={() => setEnrollCourse(course)}
                    className="absolute bottom-5 right-5 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-semibold whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity z-10"
                  >
                    Quick Enroll
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              Student <span style={{color: "#9bc400"}}>Success Stories</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {academyTestimonials.map((t) => (
              <div key={t.id} className="bg-slate-50 rounded-xl p-7 border border-slate-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-sm font-black text-white bg-slate-800 flex-shrink-0">
                    {t.avatar}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900">{t.name}</div>
                    <div className="text-xs font-semibold" style={{color: "#9bc400"}}>{t.role}</div>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-1">
                  {[1,2,3,4,5].map((s) => <i key={s} className="ri-star-fill text-[#C8F135] text-xs" />)}
                </div>
                <p className="text-xs text-slate-400 mb-3">Course: {t.course}</p>
                <p className="text-slate-600 text-sm leading-relaxed">&ldquo;{t.text}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ConsultationForm />

      {enrollCourse && <EnrollModal course={enrollCourse} onClose={() => setEnrollCourse(null)} />}
    </main>
  );
}
